import React, { useState, Suspense, useEffect } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { useDomain } from '@/shared/contexts/DomainProvider';
import { FloatingControls } from './FloatingControls';
import { ErrorBoundary } from '../base/error-boundary';
import { DomainLoadingScreen } from '../patterns/DomainLoadingScreen';
import { ToastProvider } from '@/shared/components/base/Toast';

// Import domain-specific layouts (direct imports - no lazy loading)
import BillingSidebar from '../../../domains/billing/components/layout/Sidebar';
import WorkflowSidebar from '../../../domains/workflow/components/layout/Sidebar';
import HiringSidebar from '../../../domains/hiring/components/layout/Sidebar';
import SalesSidebar from '../../../domains/sales/components/layout/Sidebar';
import IntelSidebar from '../../../domains/intel/components/layout/Sidebar';
import AdminSidebar from '../../../domains/admin/components/layout/Sidebar';
import GoalsSidebar from '../../../domains/goals/components/layout/Sidebar';
import CRMSidebar from '../../../domains/crm/components/layout/Sidebar';
import ToolsSidebar from '../../../domains/tools/components/layout/Sidebar';
import CreativeSidebar from '../../../domains/creative/components/layout/Sidebar';

interface AppLayoutProps {
  children?: React.ReactNode;
}

/**
 * Domain-specific layout content component
 * Handles different layout structures for different domains
 *
 * Note: During HMR (Hot Module Replacement), the context may be temporarily unavailable.
 * This component gracefully handles that scenario with fallbacks.
 */
const DomainLayoutContent: React.FC<{ children?: React.ReactNode }> = ({ children }) => {
  const { domainConfig } = useDomain();
  const location = useLocation();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isCollapsed, setIsCollapsed] = useState(false);

  // Update document title based on domain
  useEffect(() => {
    if (domainConfig) {
      const domainTitles: Record<string, string> = {
        'billing': 'Thrive Billing',
        'workflow': 'Thrive Workflow',
        'hiring': 'Thrive Hiring',
        'sales': 'Thrive Sales',
        'intel': 'Thrive Intel',
        'admin': 'Thrive Admin',
        'goals': 'Thrive Goals',
        'crm': 'Thrive CRM',
        'tools': 'Thrive Tools',
        'knowledge': 'Thrive Knowledge',
        'forecast': 'Thrive Forecasting',
        'creative': 'Thrive Creative'
      };
      
      const title = domainTitles[domainConfig.id] || 'Thrive Ops';
      document.title = title;
    }
  }, [domainConfig]);

  const handleMenuClose = () => {
    setIsMobileMenuOpen(false);
  };

  const handleToggleCollapse = () => {
    setIsCollapsed(!isCollapsed);
  };

  // Wrap everything in a shared container with FloatingControls.
  // ToastProvider sits at this level so useToast works in every domain.
  return (
    <ToastProvider>
    <div className="flex h-screen bg-gray-50">
      {/* Floating Controls - shared across all domains */}
      <FloatingControls
        onMenuToggle={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
        isMobileMenuOpen={isMobileMenuOpen}
      />

      {/* Domain-specific layouts */}
      {domainConfig?.id === 'billing' && (
        <div
          className="flex-1 flex flex-col overflow-hidden"
          style={{
            backgroundImage: `
              radial-gradient(circle at 20% 80%, #FBFAE9 0%, transparent 50%),
              radial-gradient(circle at 80% 20%, #FEF5F1 0%, transparent 50%),
              radial-gradient(circle at 40% 40%, #f3e2f0 0%, transparent 50%),
              linear-gradient(180deg, #f2f0f9 0%, #f7f9f0 100%)
            `
          }}
        >
          <ErrorBoundary>
            <div className="flex flex-1 overflow-hidden">
            {/* Billing Sidebar */}
            <BillingSidebar
              isOpen={isMobileMenuOpen}
              onClose={handleMenuClose}
              isCollapsed={isCollapsed}
              onToggleCollapse={handleToggleCollapse}
            />

              {/* Main content area - Dynamic margin based on sidebar state */}
              <main className={`flex-1 overflow-auto transition-all duration-300 ${
                isCollapsed ? 'lg:ml-14' : 'lg:ml-[280px]'
              }`}>
                <div className="p-6">
                  <ErrorBoundary>
                    <Suspense fallback={<DomainLoadingScreen domainName="Billing" />}>
                      {children || <Outlet key={location.pathname} />}
                    </Suspense>
                  </ErrorBoundary>
                </div>
              </main>
            </div>
          </ErrorBoundary>
        </div>
      )}

      {/* Workflow Domain Layout */}
      {domainConfig?.id === 'workflow' && (
        <div
          className="flex-1 flex flex-col overflow-hidden"
          style={{
            backgroundImage: `
              radial-gradient(circle at 20% 80%, #FBFAE9 0%, transparent 50%),
              radial-gradient(circle at 80% 20%, #FEF5F1 0%, transparent 50%),
              radial-gradient(circle at 40% 40%, #f3e2f0 0%, transparent 50%),
              linear-gradient(180deg, #f2f0f9 0%, #f7f9f0 100%)
            `
          }}
        >
          <ErrorBoundary>
            <div className="flex flex-1 overflow-hidden">
            {/* Workflow Sidebar */}
            <WorkflowSidebar
              isOpen={isMobileMenuOpen}
              onClose={handleMenuClose}
              isCollapsed={isCollapsed}
              onToggleCollapse={handleToggleCollapse}
            />

              {/* Main content area - Dynamic margin based on sidebar state */}
              <main className={`flex-1 overflow-auto transition-all duration-300 ${
                isCollapsed ? 'lg:ml-14' : 'lg:ml-[280px]'
              }`}>
                <div className="p-6">
                  <ErrorBoundary>
                    <Suspense fallback={<DomainLoadingScreen domainName="Workflow" />}>
                      {children || <Outlet />}
                    </Suspense>
                  </ErrorBoundary>
                </div>
              </main>
            </div>
          </ErrorBoundary>
        </div>
      )}

      {/* Hiring Domain Layout */}
      {domainConfig?.id === 'hiring' && (
        <div
          className="flex-1 flex flex-col overflow-hidden"
          style={{
            backgroundImage: `
              radial-gradient(circle at 20% 80%, #FBFAE9 0%, transparent 50%),
              radial-gradient(circle at 80% 20%, #FEF5F1 0%, transparent 50%),
              radial-gradient(circle at 40% 40%, #f3e2f0 0%, transparent 50%),
              linear-gradient(180deg, #f2f0f9 0%, #f7f9f0 100%)
            `
          }}
        >
          <ErrorBoundary>
            <div className="flex flex-1 overflow-hidden">
            {/* Hiring Sidebar */}
            <HiringSidebar
              isOpen={isMobileMenuOpen}
              onClose={handleMenuClose}
              isCollapsed={isCollapsed}
              onToggleCollapse={handleToggleCollapse}
            />

              {/* Main content area - Dynamic margin based on sidebar state */}
              <main className={`flex-1 overflow-auto transition-all duration-300 ${
                isCollapsed ? 'lg:ml-14' : 'lg:ml-[280px]'
              }`}>
                <div className="p-6">
                  <ErrorBoundary>
                    <Suspense fallback={<DomainLoadingScreen domainName="Hiring" />}>
                      {children || <Outlet key={location.pathname} />}
                    </Suspense>
                  </ErrorBoundary>
                </div>
              </main>
            </div>
          </ErrorBoundary>
        </div>
      )}

      {/* Sales Domain Layout */}
      {domainConfig?.id === 'sales' && (
        <div
          className="flex-1 flex flex-col overflow-hidden"
          style={{
            backgroundImage: `
              radial-gradient(circle at 20% 80%, #FBFAE9 0%, transparent 50%),
              radial-gradient(circle at 80% 20%, #FEF5F1 0%, transparent 50%),
              radial-gradient(circle at 40% 40%, #f3e2f0 0%, transparent 50%),
              linear-gradient(180deg, #f2f0f9 0%, #f7f9f0 100%)
            `
          }}
        >
          <ErrorBoundary>
            <div className="flex flex-1 overflow-hidden">
            {/* Sales Sidebar */}
            <SalesSidebar
              isOpen={isMobileMenuOpen}
              onClose={handleMenuClose}
              isCollapsed={isCollapsed}
              onToggleCollapse={handleToggleCollapse}
            />

              {/* Main content area - Dynamic margin based on sidebar state */}
              <main className={`flex-1 overflow-auto transition-all duration-300 ${
                isCollapsed ? 'lg:ml-24' : 'lg:ml-[280px]'
              }`}>
                <div className="p-6">
                  <ErrorBoundary>
                    <Suspense fallback={<DomainLoadingScreen domainName="Sales" />}>
                      {children || <Outlet key={location.pathname} />}
                    </Suspense>
                  </ErrorBoundary>
                </div>
              </main>
            </div>
          </ErrorBoundary>
        </div>
      )}

      {/* Intel Domain Layout */}
      {domainConfig?.id === 'intel' && (
        <div
          className="flex-1 flex flex-col overflow-hidden"
          style={{
            backgroundImage: `
              radial-gradient(circle at 20% 80%, #FBFAE9 0%, transparent 50%),
              radial-gradient(circle at 80% 20%, #FEF5F1 0%, transparent 50%),
              radial-gradient(circle at 40% 40%, #f3e2f0 0%, transparent 50%),
              linear-gradient(180deg, #f2f0f9 0%, #f7f9f0 100%)
            `
          }}
        >
          <ErrorBoundary>
            <div className="flex flex-1 overflow-hidden">
            {/* Intel Sidebar */}
            <IntelSidebar
              isOpen={isMobileMenuOpen}
              onClose={handleMenuClose}
              isCollapsed={isCollapsed}
              onToggleCollapse={handleToggleCollapse}
            />

              {/* Main content area - Dynamic margin based on sidebar state */}
              <main className={`flex-1 overflow-auto transition-all duration-300 ${
                isCollapsed ? 'lg:ml-14' : 'lg:ml-[280px]'
              }`}>
                <div className="p-6">
                  <ErrorBoundary>
                    <Suspense fallback={<DomainLoadingScreen domainName="Intel" />}>
                      {children || <Outlet key={location.pathname} />}
                    </Suspense>
                  </ErrorBoundary>
                </div>
              </main>
            </div>
          </ErrorBoundary>
        </div>
      )}

      {/* Goals Domain Layout */}
      {domainConfig?.id === 'goals' && (
        <div
          className="flex-1 flex flex-col overflow-hidden"
          style={{
            backgroundImage: `
              radial-gradient(circle at 20% 80%, #FBFAE9 0%, transparent 50%),
              radial-gradient(circle at 80% 20%, #FEF5F1 0%, transparent 50%),
              radial-gradient(circle at 40% 40%, #f3e2f0 0%, transparent 50%),
              linear-gradient(180deg, #f2f0f9 0%, #f7f9f0 100%)
            `
          }}
        >
          <ErrorBoundary>
            <div className="flex flex-1 overflow-hidden">
            {/* Goals Sidebar */}
            <GoalsSidebar
              isOpen={isMobileMenuOpen}
              onClose={handleMenuClose}
              isCollapsed={isCollapsed}
              onToggleCollapse={handleToggleCollapse}
            />

              {/* Main content area - Dynamic margin based on sidebar state */}
              <main className={`flex-1 overflow-auto transition-all duration-300 ${
                isCollapsed ? 'lg:ml-14' : 'lg:ml-[280px]'
              }`}>
                <div className="p-6">
                  <ErrorBoundary>
                    <Suspense fallback={<DomainLoadingScreen domainName="Goals" />}>
                      {children || <Outlet key={location.pathname} />}
                    </Suspense>
                  </ErrorBoundary>
                </div>
              </main>
            </div>
          </ErrorBoundary>
        </div>
      )}

      {/* CRM Domain Layout */}
      {domainConfig?.id === 'crm' && (
        <div
          className="flex-1 flex flex-col overflow-hidden"
          style={{
            backgroundImage: `
              radial-gradient(circle at 20% 80%, #FBFAE9 0%, transparent 50%),
              radial-gradient(circle at 80% 20%, #FEF5F1 0%, transparent 50%),
              radial-gradient(circle at 40% 40%, #f3e2f0 0%, transparent 50%),
              linear-gradient(180deg, #f2f0f9 0%, #f7f9f0 100%)
            `
          }}
        >
          <ErrorBoundary>
            <div className="flex flex-1 overflow-hidden">
            {/* CRM Sidebar */}
            <CRMSidebar
              isOpen={isMobileMenuOpen}
              onClose={handleMenuClose}
              isCollapsed={isCollapsed}
              onToggleCollapse={handleToggleCollapse}
            />

              {/* Main content area - Dynamic margin based on sidebar state */}
              <main className={`flex-1 overflow-auto transition-all duration-300 ${
                isCollapsed ? 'lg:ml-14' : 'lg:ml-[280px]'
              }`}>
                <div className="p-6">
                  <ErrorBoundary>
                    <Suspense fallback={<DomainLoadingScreen domainName="CRM" />}>
                      {children || <Outlet key={location.pathname} />}
                    </Suspense>
                  </ErrorBoundary>
                </div>
              </main>
            </div>
          </ErrorBoundary>
        </div>
      )}

      {/* Admin Domain Layout */}
      {domainConfig?.id === 'admin' && (
        <div
          className="flex-1 flex flex-col overflow-hidden"
          style={{
            backgroundImage: `
              radial-gradient(circle at 20% 80%, #FBFAE9 0%, transparent 50%),
              radial-gradient(circle at 80% 20%, #FEF5F1 0%, transparent 50%),
              radial-gradient(circle at 40% 40%, #f3e2f0 0%, transparent 50%),
              linear-gradient(180deg, #f2f0f9 0%, #f7f9f0 100%)
            `
          }}
        >
          <ErrorBoundary>
            <div className="flex flex-1 overflow-hidden">
              {/* Admin Sidebar */}
              <AdminSidebar
                isOpen={isMobileMenuOpen}
                onClose={handleMenuClose}
              />

              {/* Main Content Area */}
              <main className="flex-1 overflow-auto lg:ml-64">
                <div className="p-6">
                  <ErrorBoundary>
                    <Suspense fallback={<DomainLoadingScreen domainName="Administration" />}>
                      {children || <Outlet key={location.pathname} />}
                    </Suspense>
                  </ErrorBoundary>
                </div>
              </main>
            </div>
          </ErrorBoundary>
        </div>
      )}

      {/* Tools Domain Layout */}
      {domainConfig?.id === 'tools' && (
        <div
          className="flex-1 flex flex-col overflow-hidden"
          style={{
            backgroundImage: `
              radial-gradient(circle at 20% 80%, #FBFAE9 0%, transparent 50%),
              radial-gradient(circle at 80% 20%, #FEF5F1 0%, transparent 50%),
              radial-gradient(circle at 40% 40%, #f3e2f0 0%, transparent 50%),
              linear-gradient(180deg, #f2f0f9 0%, #f7f9f0 100%)
            `
          }}
        >
          <ErrorBoundary>
            <div className="flex flex-1 overflow-hidden">
            {/* Tools Sidebar */}
            <ToolsSidebar
              isOpen={isMobileMenuOpen}
              onClose={handleMenuClose}
              isCollapsed={isCollapsed}
              onToggleCollapse={handleToggleCollapse}
            />

              {/* Main content area - Dynamic margin based on sidebar state */}
              <main className={`flex-1 overflow-auto transition-all duration-300 ${
                isCollapsed ? 'lg:ml-14' : 'lg:ml-[280px]'
              }`}>
                <div className="p-6">
                  <ErrorBoundary>
                    <Suspense fallback={<DomainLoadingScreen domainName="Tools" />}>
                      {children || <Outlet key={location.pathname} />}
                    </Suspense>
                  </ErrorBoundary>
                </div>
              </main>
            </div>
          </ErrorBoundary>
        </div>
      )}

      {/* Creative Domain Layout */}
      {domainConfig?.id === 'creative' && (
        <div
          className="flex-1 flex flex-col overflow-hidden"
          style={{
            backgroundImage: `
              radial-gradient(circle at 20% 80%, #FBFAE9 0%, transparent 50%),
              radial-gradient(circle at 80% 20%, #FEF5F1 0%, transparent 50%),
              radial-gradient(circle at 40% 40%, #f3e2f0 0%, transparent 50%),
              linear-gradient(180deg, #f2f0f9 0%, #f7f9f0 100%)
            `
          }}
        >
          <ErrorBoundary>
            <div className="flex flex-1 overflow-hidden">
            <CreativeSidebar
              isOpen={isMobileMenuOpen}
              onClose={handleMenuClose}
              isCollapsed={isCollapsed}
              onToggleCollapse={handleToggleCollapse}
            />

              <main className={`flex-1 overflow-auto transition-all duration-300 ${
                isCollapsed ? 'lg:ml-14' : 'lg:ml-[280px]'
              }`}>
                <div className="p-6">
                  <ErrorBoundary>
                    <Suspense fallback={<DomainLoadingScreen domainName="Creative" />}>
                      {children || <Outlet key={location.pathname} />}
                    </Suspense>
                  </ErrorBoundary>
                </div>
              </main>
            </div>
          </ErrorBoundary>
        </div>
      )}

      {/* Forecast Domain Layout */}
      {domainConfig?.id === 'forecast' && (
        <div
          className="flex-1 flex flex-col overflow-hidden"
          style={{
            backgroundImage: `
              radial-gradient(circle at 20% 80%, #FBFAE9 0%, transparent 50%),
              radial-gradient(circle at 80% 20%, #FEF5F1 0%, transparent 50%),
              radial-gradient(circle at 40% 40%, #f3e2f0 0%, transparent 50%),
              linear-gradient(180deg, #f2f0f9 0%, #f7f9f0 100%)
            `
          }}
        >
          <ErrorBoundary>
            <main className="flex-1 overflow-hidden">
              <Suspense fallback={<DomainLoadingScreen domainName="Forecasting" />}>
                {children || <Outlet key={location.pathname} />}
              </Suspense>
            </main>
          </ErrorBoundary>
        </div>
      )}

      {/* Knowledge Domain Layout */}
      {domainConfig?.id === 'knowledge' && (
        <div
          className="flex-1 flex flex-col overflow-hidden"
          style={{
            backgroundImage: `
              radial-gradient(circle at 20% 80%, #FBFAE9 0%, transparent 50%),
              radial-gradient(circle at 80% 20%, #FEF5F1 0%, transparent 50%),
              radial-gradient(circle at 40% 40%, #f3e2f0 0%, transparent 50%),
              linear-gradient(180deg, #f2f0f9 0%, #f7f9f0 100%)
            `
          }}
        >
          <ErrorBoundary>
            <main className="flex-1 overflow-hidden">
              <Suspense fallback={<DomainLoadingScreen domainName="Knowledge" />}>
                {children || <Outlet key={location.pathname} />}
              </Suspense>
            </main>
          </ErrorBoundary>
        </div>
      )}

      {/* Fallback Layout for Unknown Domains */}
      {!['billing', 'workflow', 'hiring', 'sales', 'intel', 'admin', 'goals', 'crm', 'tools', 'knowledge', 'forecast', 'creative'].includes(domainConfig?.id || '') && (
        <div
          className="flex-1 flex flex-col overflow-hidden"
          style={{
            background: 'radial-gradient(ellipse 140% 120% at 10% 20%, #e8edf3 0%, #f1f5f9 35%, #f9fafb 100%)'
          }}
        >
          <ErrorBoundary>
            {/* Main Content Area */}
            <main className="flex-1 overflow-auto">
              <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
                <ErrorBoundary>
                  <Suspense fallback={<DomainLoadingScreen />}>
                    {children || <Outlet key={location.pathname} />}
                  </Suspense>
                </ErrorBoundary>
              </div>
            </main>
          </ErrorBoundary>
        </div>
      )}
    </div>
    </ToastProvider>
  );
};

/**
 * AppLayout component - Main application layout wrapper
 *
 * Features:
 * - Uses domain context from parent DomainProvider (in App.tsx)
 * - Includes shared TopBar with domain switcher
 * - Handles domain-specific layouts (billing with sidebar, others without)
 * - Handles error boundaries and loading states
 * - Supports mobile responsive design
 *
 * Note: DomainProvider is now at App level for stability across route changes
 */
export const AppLayout: React.FC<AppLayoutProps> = ({ children }) => {
  return (
    <DomainLayoutContent>
      {children}
    </DomainLayoutContent>
  );
};

